// ---------------------------------------------------------------------------
// Single source of truth for the one-pager.
// Edit copy here — no need to touch components.
//
// Wording rules come from the authority claim ledger:
//   · "144,000+ users" and "six-figure revenue" are COMBINED across all apps,
//     never attributed to a single product.
//   · 67+ apps = built and shipped (wording confirmed by Ahsan, 2026-09-21).
//   · Never claim sole build, and never invent client names, currency or dates.
// ---------------------------------------------------------------------------

export const profile = {
  name: "Ahsan Usman",
  role: "AI Full-Stack Engineer & Mobile App Developer",
  kicker: "Mobile Apps · App Growth · AI",
  headline: ["AI Full-Stack Engineer & Mobile App Developer"],
  sub: "9+ years building web and mobile apps for Android, iOS, and desktop. I lead full-stack architecture, launch, and growth using SEO/ASO and performance marketing.",
  avatar: "/avatar/ahsan-portrait.webp",
};

export const focus = [
  "SaaS Engineering",
  "AI Workflows",
  "Payments & Subscriptions",
  "Tracking & Analytics",
  "App Growth / ASO",
  "Product Operations",
];

// Aggregate figures — combined portfolio numbers, per the claim ledger.
// `icon` keys map to the SVG set in Sections.jsx.
export const proof = [
  { icon: "work", value: "9+", label: "Years building web and mobile apps for Android, iOS." },
  { icon: "apps", value: "67+", label: "Web and mobile apps built and shipped" },
  { icon: "users", value: "144K+", label: "Combined users reached across those apps" },
  { icon: "star", value: "4.8+", label: "Combined app ratings across the portfolio" },
];

export const recognition = [
  { icon: "apple", label: "Apple App Store App of the Day recognition" },
  { icon: "play", label: "Google Play Top 10 placement" },
  { icon: "revenue", label: "Six-figure combined revenue" },
  { icon: "solo", label: "Individual engineer, not an agency" },
];

export const nav = [
  { label: "About", href: "#about" },
  { label: "What I Do", href: "#what-i-do" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "Skills", href: "#skills" },
  { label: "Contact", href: "#contact" },
];

export const about = {
  label: "About",
  heading: "I build and grow apps.",
  paragraphs: [
    "9+ years of experience, 67+ apps shipped, 144K+ combined users. I take a product the whole way: architecture and backend, subscriptions and payments, then growth through ASO, store listings, Apple Ads, and Google App campaigns. Most of my work sits where engineering meets revenue.",
  ],
  /* Panel icons are local transparent SVGs in public/tools/<key>.svg —
     Simple Icons marks plus the official Clarity and Superwall logos. */
  tools: [
    { icons: ["flutter"], label: "Flutter" },
    { icons: ["react-native"], label: "React Native" },
    { icons: ["nextjs"], label: "Next.js" },
    { icons: ["revenuecat", "superwall"], label: "RevenueCat & Superwall" },
    { icons: ["stripe"], label: "Stripe" },
    { icons: ["appstore", "googleplay"], label: "App Store & Google Play" },
    { icons: ["googleanalytics", "clarity"], label: "Google Analytics & Microsoft Clarity" },
    { icons: ["googlesearchconsole"], label: "Google Search Console" },
    { icons: ["search"], label: "AEO, SEO & ASO" },
  ],
};

/* Three ways the work usually lands. Framing came from "How I Create Leverage"
   on GitHub; renamed Build / Launch / Market on 2026-09-21. */
export const services = {
  label: "What I Do",
  heading: "Build. Launch. Market.",
  items: [
    {
      index: "01",
      icon: "build",
      title: "Build",
      body: "Turn product ideas into working systems: MVPs, mobile apps, dashboards, APIs, AI tools, internal tools, and subscription products.",
    },
    {
      index: "02",
      icon: "launch",
      title: "Launch",
      body: "Get it live: App Store and Google Play submission, store review compliance, onboarding and paywall flow, tracking, and a release process that holds up over time.",
    },
    {
      index: "03",
      icon: "market",
      title: "Market",
      body: "Bring in users and revenue: ASO, store listings, Apple Ads, Google App campaigns, paywall and onboarding tests, and the analytics that show which channels pay back.",
    },
  ],
};

// ---------------------------------------------------------------------------
// WORK — case studies.
// Add more by pushing objects into `items`.
// ---------------------------------------------------------------------------
export const projects = {
  /* No eyebrow label here: the nav already says Portfolio. */
  heading: "Portfolio.",
  items: [
    {
      slug: "nwfit-ai-fitness-app",
      title: "NWFit · AI Fitness App",
      category: "Product Engineering · Mobile",
      period: "August 2023",
      client: "NWFit",
      role: "AI Full-Stack Engineer",
      image: "/work/nwfit-ai-fitness-app.webp",
      imageW: 1448,
      imageH: 1086,
      imageAlt: "NWFit AI fitness app case study graphic with three phone screens",
      card: "Built and scaled an AI-powered fitness platform to 140K+ users.",
      summary:
        "NWFit is an AI-powered fitness platform that grew to 140K+ users in under two years across iOS, Android, and web. I worked as an AI Full-Stack Engineer, overseeing development and reviewing architecture and code, stepping in to write features when the build needed to scale.",
      did: [
        "Flutter mobile app for iOS and Android",
        "Next.js web app with Stripe payments and subscriptions",
        "Express.js and MongoDB backend with APIs",
        "AI workout recommendation engine built with Python and FastAPI",
        "RevenueCat and Superwall for subscriptions, paywalls, and A/B testing",
        "HealthKit, Google ML Kit, and fitness and activity tracking",
        "Firebase analytics and push notifications",
        "Meta Pixel and CAPI, GA4, Google Ads, and Microsoft Clarity",
      ],
      results: [
        "140K+ users in under two years",
        "Shipped on iOS, Android, and web from one codebase",
        "AI coach, subscriptions, and analytics in production",
      ],
      tags: ["Flutter", "Next.js", "Express.js", "MongoDB", "FastAPI", "Stripe", "RevenueCat", "Firebase"],
    },
    {
      slug: "saas-lead-recovery",
      title: "SaaS Lead Recovery Agent",
      category: "AI Automation · SaaS",
      period: "Q2 2025",
      client: "B2B SaaS startup",
      role: "AI Automation Engineer",
      image: "/work/saas-lead-recovery.webp",
      imageW: 1000,
      imageH: 750,
      imageAlt: "SaaS lead recovery agent dashboard showing automated lead qualification and booking",
      card: "Recovered $47K in lost revenue by automating lead qualification and booking.",
      summary:
        "A B2B SaaS startup was hemorrhaging leads—cold signups, abandoned carts, and unconverted trial users were slipping through the cracks. I built an AI agent that runs 24/7, qualifies inbound leads, auto-books meetings, and follows up with cold prospects without human intervention.",
      did: [
        "Built lead qualification agent with GPT-4 and custom scoring logic",
        "Integrated with Calendly for auto-booking qualified meetings",
        "Designed follow-up sequences for cold leads with personalized outreach",
        "Connected to CRM (HubSpot) for pipeline visibility and reporting",
        "Set up webhook triggers for real-time lead event processing",
      ],
      results: [
        "$47K recovered in first 90 days",
        "24/7 lead qualification and booking",
        "Cold-lead follow-up on autopilot",
      ],
      tags: ["AI agents", "GPT-4", "Calendly", "HubSpot", "Lead generation", "Automation"],
    },
    {
      slug: "clinic-network-ai-agent",
      title: "Clinic Network AI Agent",
      category: "AI Automation · Healthcare",
      period: "Q3 2025",
      client: "5-location clinic network",
      role: "AI Automation Engineer",
      image: "/work/clinic-network-ai-agent.webp",
      imageW: 1000,
      imageH: 750,
      imageAlt: "Clinic network AI agent handling appointment booking and insurance questions",
      card: "Deployed an AI agent across 5 clinic locations for booking, insurance, and follow-ups.",
      summary:
        "A multi-location clinic network was struggling with phone-based booking, insurance questions, and missed follow-ups across 5 locations. I built an AI agent that handles appointment scheduling, answers insurance queries, and sends automated follow-up reminders—freeing staff to focus on patient care.",
      did: [
        "Built appointment booking agent integrated with clinic scheduling systems",
        "Designed insurance verification flow with carrier lookup",
        "Automated follow-up reminders for post-visit check-ins",
        "Deployed across 5 locations with location-specific logic",
        "Set up fallback to human staff for complex cases",
      ],
      results: [
        "5-location clinic network onboarded",
        "Appointment booking fully automated",
        "Insurance questions handled 24/7",
      ],
      tags: ["AI agents", "Healthcare", "Appointment booking", "Insurance verification", "Patient engagement", "Multi-location"],
    },
    {
      slug: "real-estate-admin-automation",
      title: "Real Estate Admin Automation",
      category: "AI Automation · Real Estate",
      period: "Q1 2025",
      client: "Real estate brokerage",
      role: "AI Automation Engineer",
      image: "/work/real-estate-admin-automation.webp",
      imageW: 1000,
      imageH: 750,
      imageAlt: "Real estate admin automation system handling 200+ monthly inquiries",
      card: "Automated 200+ monthly inquiries, saving 40 hours/week of manual admin work.",
      summary:
        "A real estate brokerage was drowning in manual admin work—200+ inquiries per month handled by hand, spreadsheets, and phone calls. I built an AI automation layer that triages incoming inquiries, qualifies leads, and routes them to agents, eliminating 40 hours of weekly busywork.",
      did: [
        "Built inquiry triage agent that categorizes and scores inbound leads",
        "Automated routing to the right agent based on property type and location",
        "Integrated with MLS data feeds for real-time property matching",
        "Set up automated follow-up sequences for warm leads",
        "Created dashboard for pipeline visibility and conversion tracking",
      ],
      results: [
        "200+ inquiries/month automated",
        "40 hours/week of manual work eliminated",
        "Closings increased by 18%",
      ],
      tags: ["AI automation", "Real estate", "Lead routing", "MLS integration", "CRM", "Workflow automation"],
    },
    {
      slug: "clinic-network-ai-agent",
      title: "Clinic Network AI Agent",
      category: "AI Automation · Healthcare",
      period: "Q3 2025",
      client: "5-location clinic network",
      role: "AI Automation Engineer",
      image: "/work/clinic-network-ai-agent.webp",
      imageW: 1000,
      imageH: 750,
      imageAlt: "Clinic network AI agent handling appointment booking and insurance questions",
      card: "Deployed an AI agent across 5 clinic locations for booking, insurance, and follow-ups.",
      summary:
        "A multi-location clinic network was struggling with phone-based booking, insurance questions, and missed follow-ups across 5 locations. I built an AI agent that handles appointment scheduling, answers insurance queries, and sends automated follow-up reminders—freeing staff to focus on patient care.",
      did: [
        "Built appointment booking agent integrated with clinic scheduling systems",
        "Designed insurance verification flow with carrier lookup",
        "Automated follow-up reminders for post-visit check-ins",
        "Deployed across 5 locations with location-specific logic",
        "Set up fallback to human staff for complex cases",
      ],
      results: [
        "5-location clinic network onboarded",
        "Appointment booking fully automated",
        "Insurance questions handled 24/7",
      ],
      tags: ["AI agents", "Healthcare", "Appointment booking", "Insurance verification", "Patient engagement", "Multi-location"],
    },
    {
      slug: "real-estate-admin-automation",
      title: "Real Estate Admin Automation",
      category: "AI Automation · Real Estate",
      period: "Q1 2025",
      client: "Real estate brokerage",
      role: "AI Automation Engineer",
      image: "/work/real-estate-admin-automation.webp",
      imageW: 1000,
      imageH: 750,
      imageAlt: "Real estate admin automation system handling 200+ monthly inquiries",
      card: "Automated 200+ monthly inquiries, saving 40 hours/week of manual admin work.",
      summary:
        "A real estate brokerage was drowning in manual admin work—200+ inquiries per month handled by hand, spreadsheets, and phone calls. I built an AI automation layer that triages incoming inquiries, qualifies leads, and routes them to agents, eliminating 40 hours of weekly busywork.",
      did: [
        "Built inquiry triage agent that categorizes and scores inbound leads",
        "Automated routing to the right agent based on property type and location",
        "Integrated with MLS data feeds for real-time property matching",
        "Set up automated follow-up sequences for warm leads",
        "Created dashboard for pipeline visibility and conversion tracking",
      ],
      results: [
        "200+ inquiries/month automated",
        "40 hours/week of manual work eliminated",
        "Closings increased by 18%",
      ],
      tags: ["AI automation", "Real estate", "Lead routing", "MLS integration", "CRM", "Workflow automation"],
    },
    {
      slug: "nwfit-ai",
      title: "NWFit · User Acquisition & Revenue Growth",
      category: "App Growth · Paid UA · Monetization",
      period: "May 2025",
      client: "NWFit",
      role: "Acquisition strategy, ASO, paid media, onboarding, and monetization",
      image: "/work/nwfit-aso-before-after.webp",
      imageW: 1024,
      imageH: 768,
      imageAlt: "NWFit Google Play listing before and after the app store optimization work",
      card: "Scaled NWFit from 3K to 140K+ users while improving acquisition cost and subscription conversion.",
      summary:
        "NWFit is a subscription-based AI fitness app available on iOS, Android, and the web. I led user acquisition and monetization across app store optimization, paid advertising, the web-to-app sales funnel, onboarding, subscription paywalls, and lifecycle analytics. This work helped scale NWFit from 3,000 to 140K+ users while improving the journey from first visit to paid subscription.",
      did: [
        "Researched 10+ competing fitness apps and built a keyword strategy around 50+ high-intent search terms",
        "Rewrote the app title, subtitle, short description, and full store listing for Google Play and the Apple App Store",
        "Created benefit-led screenshots, feature graphics, and alternate app icons, then tested creative combinations to improve store conversion",
        "Localized store metadata and creative assets for five priority markets",
        "Managed Apple Ads campaigns targeting high-intent searches in the App Store",
        "Managed Google Ads across App campaigns, Search, Display, and Performance Max to acquire new users and re-engage existing audiences",
        "Built and optimized the NWFit web-to-app landing page and sales funnel",
        "Implemented RevenueCat and Superwall to launch and test conversion-focused subscription paywalls",
        "Redesigned onboarding around activation and A/B tested the complete onboarding and paywall journey",
        "Connected advertising, app store, subscription, and product analytics to optimize campaigns for paying subscribers rather than installs alone",
        "Added well-timed rating and review prompts to collect feedback from active users",
      ],
      results: [
        "Grew the user base from 3,000 to 140K+",
        "Reduced cost per install by 30%",
        "Increased install-to-paid subscription conversion rate",
        "Increased average revenue per user (ARPU)",
        "Increased customer lifetime value (LTV)",
        "Google Play downloads grew from 10+ to 5K+ during the ASO engagement",
        "Earned a 4.8★ Google Play rating from 490 reviews",
        "Earned a 4.9★ Apple App Store rating",
        "Ranked for 30+ competitive fitness search terms",
        "Reached the Top 10 in a Health & Fitness subcategory",
      ],
      tags: ["Paid user acquisition", "ASO", "Apple Ads", "Google Ads", "Performance Max", "RevenueCat", "Superwall", "A/B testing", "Subscription growth"],
      links: [
        { label: "Web-to-app sales funnel", href: "https://nwfit.ai/start" },
        { label: "View on the App Store", href: "https://apps.apple.com/us/app/nwfit-fitness-weight-loss/id6475285888" },
        { label: "View on Google Play", href: "https://play.google.com/store/apps/details?id=com.ninjawarriorsmeta.nwfit" },
      ],
    },
    {
      slug: "budgetbee-aso",
      title: "BudgetBee · 340% Organic Install Growth",
      category: "App Growth · ASO · Paid UA",
      period: "4-month engagement",
      client: "Frugal Living LLC",
      role: "ASO strategy, Apple Ads, Google App campaigns",
      image: "/work/budgetbee-aso-before-after.webp",
      imageW: 1024,
      imageH: 768,
      imageAlt: "BudgetBee Google Play listing before and after the store listing rebuild",
      card: "Raised organic installs 340% and grew Google Play from 50+ to 100K+ downloads.",
      summary:
        "BudgetBee is a personal finance app on Google Play and the Apple App Store. It had little organic visibility and depended on paid campaigns with a high cost per install. I built and ran a four-month ASO and paid acquisition program to improve discoverability, conversion, and subscriber growth.",
      did: [
        "Analyzed 15+ competing budgeting apps, including Mint, YNAB, and PocketGuard",
        "Built an 80+ keyword set across six markets: US, UK, Canada, Australia, India, and Germany",
        "Rewrote the title, subtitle, short description, and full description for both stores",
        "Redesigned screenshot sets around trust, AI budgeting, automated insights, and savings goals",
        "Created three Custom Product Page variants for Apple Ads",
        "Ran in-app events for the New Year Budget Challenge and Savings Streak",
        "Managed Apple Ads and Google App campaigns for installs and in-app purchases",
      ],
      results: [
        "Organic installs increased 340% over four months",
        "Google Play rating of 4.9★ from 1,241 reviews",
        "Downloads grew from 50+ to 100K+",
        "Ranked #3 for 'budget tracker' in a Finance subcategory",
        "Ranked for 65+ search terms across six markets",
        "User acquisition cost fell 30% while ROAS increased",
        "Install-to-subscription conversion, LTV, and revenue per user increased",
      ],
      tags: ["ASO", "Apple Ads", "Google App campaigns", "Custom Product Pages", "In-app events", "Localization", "Finance apps"],
    },
    {
      slug: "wedding-biodata-maker",
      title: "Wedding Biodata Maker · Web App",
      category: "Product Engineering · Web",
      period: "Full-stack build",
      client: "Wedding house client",
      role: "Full-Stack Developer",
      image: "/work/wedding-biodata-maker.webp",
      imageW: 1000,
      imageH: 750,
      imageAlt: "Wedding biodata maker web app with template selection and editing tools",
      card: "A browser-based marriage biodata maker with 100K+ biodatas created.",
      summary:
        "A full-stack marriage biodata maker built for a major wedding house client. Users pick from 17+ templates, customize fields such as Rashi, Nakshatra, and Gotra, crop photos, and export polished files. Everything runs in the browser with zero server-side storage, so personal details never leave the device.",
      did: [
        "Built the app in Next.js and TypeScript",
        "17+ biodata templates with customizable fields",
        "Canvas API image cropping and processing",
        "PDF, Word, PNG, and JPG export",
        "Browser-based rendering with zero server-side storage",
        "Responsive layout and multi-language support",
      ],
      results: [
        "100K+ biodatas created",
        "17+ templates shipped",
        "Four export formats from a single flow",
      ],
      tags: ["Next.js", "TypeScript", "Canvas API", "PDF generation", "Image processing", "Privacy-first"],
    },
  ],
  placeholderCount: 0,
};

/* Tooling — grouped from the GitHub skill map. */
export const toolkit = {
  label: "Skills",
  heading: "The stack I work in.",
  /* Every item carries a transparent brand mark from public/tools/<icon>.svg.
     Brand marks keep their own colour; concept items use a hand-made cyan glyph. */
  groups: [
    {
      name: "Mobile",
      items: [
        { label: "Flutter", icon: "flutter" },
        { label: "Dart", icon: "dart" },
        { label: "React Native", icon: "react-native" },
        { label: "iOS", icon: "ios" },
        { label: "Android", icon: "android" },
        { label: "SwiftUI", icon: "swiftui" },
      ],
    },
    {
      name: "Web & frontend",
      items: [
        { label: "Next.js", icon: "nextjs" },
        { label: "React", icon: "react" },
        { label: "Vue.js", icon: "vue" },
        { label: "Nuxt", icon: "nuxt" },
        { label: "TailwindCSS", icon: "tailwindcss" },
        { label: "TypeScript", icon: "typescript" },
        { label: "JavaScript", icon: "javascript" },
      ],
    },
    {
      name: "Backend & APIs",
      items: [
        { label: "Node.js", icon: "node" },
        { label: "Express.js", icon: "express" },
        { label: "Python", icon: "python" },
        { label: "FastAPI", icon: "fastapi" },
        { label: "Django", icon: "django" },
        { label: "REST APIs", icon: "api" },
      ],
    },
    {
      name: "Databases & storage",
      items: [
        { label: "PostgreSQL", icon: "postgresql" },
        { label: "MySQL", icon: "mysql" },
        { label: "MongoDB", icon: "mongodb" },
        { label: "Firebase", icon: "firebase" },
        { label: "Supabase", icon: "supabase" },
      ],
    },
    {
      name: "Payments & growth",
      items: [
        { label: "Stripe", icon: "stripe" },
        { label: "PayPal", icon: "paypal" },
        { label: "RevenueCat", icon: "revenuecat" },
        { label: "Superwall", icon: "superwall" },
        { label: "In-app purchases", icon: "iap" },
        { label: "GA4", icon: "googleanalytics" },
        { label: "Google Tag Manager", icon: "gtm" },
        { label: "Meta Pixel & CAPI", icon: "meta" },
        { label: "AppsFlyer", icon: "appsflyer" },
      ],
    },
    {
      name: "AI & automation",
      items: [
        { label: "OpenAI API", icon: "openai" },
        { label: "AI chatbots", icon: "chatbot" },
        { label: "Recommendation flows", icon: "recommend" },
        { label: "Internal tools", icon: "internal" },
        { label: "Webhooks", icon: "webhook" },
        { label: "Cron jobs", icon: "cron" },
        { label: "Event handling", icon: "events" },
        { label: "Twilio call systems", icon: "twilio" },
      ],
    },
  ],
};

/* The contact address is deliberately NOT stored here. It is assembled at
   runtime in Components/Email.jsx so no literal address ships in the HTML or
   the bundle for scrapers to harvest. */
export const contact = {
  label: "Contact",
  heading: "Building something new, or improving what is already live?",
  sub: "Send a rough spec, a store link, or a Figma file. I'll come back with architecture, scope, and timeline.",
  ctaLabel: "Email me",
  secondaryLabel: "View GitHub",
  secondaryHref: "https://github.com/theahsanusman",
};

export const footer = {
  line: "AI full-stack engineer and mobile app developer. I build, launch, and market apps.",
};

// Fill in the handles you use — empty strings stay hidden.
export const socials = [
  { label: "GitHub", href: "https://github.com/theahsanusman", icon: "github" },
  { label: "LinkedIn", href: "https://www.linkedin.com/in/theahsanusman", icon: "linkedin" },
];

export const siteMeta = {
  title: "Ahsan Usman | AI Full-Stack Engineer & Mobile App Developer",
  description:
    "Ahsan Usman is an AI full-stack SaaS engineer building mobile apps, SaaS platforms, APIs, automation, payments, and tracking systems.",
  url: "https://www.ahsanusman.com",
};
